import axios, { AxiosInstance } from 'axios';

// ============================================
// TYPE DEFINITIONS
// ============================================

// PNR Types
interface PNRPassenger {
  name: string;
  status: string;
  seat: string;
  berthType: string;
  confirmationProbability: string;
}

interface PNRData {
  pnr: string;
  status: string;
  train: {
    number: string;
    name: string;
    class: string;
  };
  journey: {
    from: {
      name: string;
      code: string;
      platform: string;
    };
    to: {
      name: string;
      code: string;
      platform: string;
    };
    departure: string;
    arrival: string;
    duration: string | null;
  };
  chart: {
    status: string;
    message: string;
  };
  passengers: PNRPassenger[];
  lastUpdated: string;
}

// Train Info Types
interface TrainInfo {
  train_no: string;
  train_name: string;
  from_stn_name: string;
  from_stn_code: string;
  to_stn_name: string;
  to_stn_code: string;
  from_time: string;
  to_time: string;
  travel_time: string;
  running_days: string;
  type: string;
  train_id: string;
}

interface RouteStation {
  stnCode: string;
  stnName: string;
  arrival: string;
  departure: string;
  halt: string;
  haltMinutes: number;
  distance: string;
  day: string;
  platform: string | number;
  coordinates: {
    latitude: number;
    longitude: number;
  } | null;
}

interface TrainInfoData {
  trainInfo: TrainInfo;
  route: RouteStation[];
}

// Train Tracking Types
interface TrainTrackingData {
  [key: string]: any;
}

// Live at Station Types
interface LiveAtStationData {
  [key: string]: any;
}

// Search Trains Types
interface SearchTrainResult {
  train_no: string;
  train_name: string;
  source_stn_name: string;
  source_stn_code: string;
  dstn_stn_name: string;
  dstn_stn_code: string;
  from_stn_name: string;
  from_stn_code: string;
  to_stn_name: string;
  to_stn_code: string;
  from_time: string;
  to_time: string;
  travel_time: string;
  running_days: string;
  distance: string;
  halts: number;
}

interface SearchTrainsData {
  success: boolean;
  time_stamp: number;
  data: SearchTrainResult[] | string;
}

// Generic API Response
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// ============================================
// API SERVICE CLASS
// ============================================

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      // FIXED: Remove /api from baseURL since your backend doesn't use it
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:4000',
      timeout: 15000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor with debugging
    this.api.interceptors.request.use(
      (config) => {
        console.log('🚀 API Request:', config.method?.toUpperCase(), config.url);
        console.log('📦 Request Data:', config.data);
        
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        console.error('❌ Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor with debugging
    this.api.interceptors.response.use(
      (response) => {
        console.log('✅ API Response:', response.status, response.config.url);
        console.log('📨 Response Data:', response.data);
        return response;
      },
      (error) => {
        console.error('❌ Response Error:', error.response?.status, error.config?.url);
        console.error('❌ Error Details:', error.response?.data);
        
        if (error.response?.status === 401) {
          localStorage.removeItem('authToken');
        }
        return Promise.reject(error);
      }
    );
  }

  // ============================================
  // 1. PNR STATUS API
  // ============================================
  async getPNRStatus(pnr: string): Promise<ApiResponse<PNRData>> {
    try {
      console.log('🔍 Fetching PNR Status for:', pnr);
      const response = await this.api.get<ApiResponse<PNRData>>(`/pnr/${pnr}`);
      
      console.log('📊 Raw PNR Response:', response.data);
      
      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to fetch PNR status',
        };
      }
    } catch (error: any) {
      console.error('💥 PNR API Error:', error);
      console.error('💥 Error Response:', error.response?.data);
      
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to fetch PNR status. Please check your internet connection.',
      };
    }
  }

  // ============================================
  // 2. TRAIN INFO API (with Route)
  // ============================================
  async getTrainInfo(trainNumber: string): Promise<ApiResponse<TrainInfoData>> {
    try {
      console.log('🔍 Fetching Train Info for:', trainNumber);
      const response = await this.api.get<ApiResponse<TrainInfoData>>(`/train/${trainNumber}`);
      
      console.log('📊 Raw Train Info Response:', response.data);
      
      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to fetch train info',
        };
      }
    } catch (error: any) {
      console.error('💥 Train Info API Error:', error);
      console.error('💥 Error Response:', error.response?.data);
      
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to fetch train info',
      };
    }
  }


// ============================================
// 3. TRACK TRAIN (Live Status)
// ============================================
async trackTrain(trainNumber: string, date: string): Promise<ApiResponse<TrainTrackingData>> {
  try {
    console.log('🔍 Tracking Train Request:');
    console.log('   - Train Number:', trainNumber);
    console.log('   - Date:', date);
    
    const requestBody = {
      trainNumber,
      date, // dd-mm-yyyy format
    };
    
    console.log('   - Request Body:', JSON.stringify(requestBody));
    
    const response = await this.api.post<any>('/track', requestBody);

    console.log('📊 Full Response:', JSON.stringify(response.data, null, 2));

    // Handle different response structures
    let responseData = null;
    
    // Check multiple possible structures
    if (response.data) {
      // Direct data
      if (response.data.stations) {
        responseData = response.data;
      }
      // Nested in data property
      else if (response.data.data && response.data.data.stations) {
        responseData = response.data.data;
      }
      // Nested in body property
      else if (response.data.body && response.data.body.stations) {
        responseData = response.data.body;
      }
      // Success flag with data
      else if (response.data.success && response.data.data) {
        responseData = response.data.data;
      }
      // Just return what we got
      else {
        responseData = response.data;
      }
    }

    console.log('✅ Processed Data:', JSON.stringify(responseData, null, 2));

    if (responseData) {
      return {
        success: true,
        data: responseData,
      };
    }

    return {
      success: false,
      error: 'No train data received from server',
    };
  } catch (error: any) {
    console.error('💥 Track Train API Error:', error);
    console.error('💥 Error Response:', JSON.stringify(error.response?.data, null, 2));
    
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to track train',
    };
  }
}

  // ============================================
  // 4. LIVE AT STATION
  // ============================================
  async getLiveAtStation(stnCode: string): Promise<ApiResponse<LiveAtStationData>> {
    try {
      console.log('🔍 Fetching Live at Station:', stnCode);
      const response = await this.api.post<ApiResponse<LiveAtStationData>>('/at-station', {
        stnCode,
      });

      console.log('📊 Raw Station Response:', response.data);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data,
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to fetch station data',
        };
      }
    } catch (error: any) {
      console.error('💥 Live at Station API Error:', error);
      console.error('💥 Error Response:', error.response?.data);
      
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to fetch station data',
      };
    }
  }

  // ============================================
  // 5. SEARCH TRAINS BETWEEN STATIONS
  // ============================================
  async searchTrains(from: string, to: string): Promise<ApiResponse<SearchTrainsData>> {
    try {
      console.log('🔍 Searching Trains from:', from, 'to:', to);
      const response = await this.api.post<SearchTrainsData>('/search', {
        from,
        to,
      });

      console.log('📊 Raw Search Response:', response.data);

      if (response.data.success) {
        return {
          success: true,
          data: response.data,
        };
      } else {
        return {
          success: false,
          error: typeof response.data.data === 'string' ? response.data.data : 'Failed to search trains',
        };
      }
    } catch (error: any) {
      console.error('💥 Search Trains API Error:', error);
      console.error('💥 Error Response:', error.response?.data);
      
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to search trains',
      };
    }
  }
}

export default new ApiService();

// Export types for use in components
export type {
  PNRData,
  PNRPassenger,
  TrainInfo,
  RouteStation,
  TrainInfoData,
  TrainTrackingData,
  LiveAtStationData,
  SearchTrainResult,
  SearchTrainsData,
  ApiResponse,
};